import React, { FC, useState, useEffect, useCallback, MouseEvent } from 'react';
import styles from './styles.module.css';
import BulkSeverity, { Priority } from './bulk_severity';
import BulkStatus, { Status } from './bulk_status';
import BulkDueDate from './bulk_due_date';
import { Button, Modal } from 'antd';
import { X as Close } from 'lucide-react';
import cx from 'classnames';
import { useProjectState } from '../../../../../clients/providers/Project';
import BulkMultiple, { Assignee, TagType } from './bulk_mutliple';
import {
  BulkRequestActions,
  NewStateUpdateBulkRequestArgs,
  UpdateState
} from 'appJS/models/BulkRequest';
import { processStateUpdateBulkRequest } from '../createBulkRequest';
import { MAX_NUMBER_OF_TASKS } from './../Cards';
import StatusBar from './status_bar';
import camelcaseKeys from 'camelcase-keys';
import { getSortedAssignableUsers } from 'appJS/utils/getAssignableUsers';
import BulkVisibility from './bulk_visibility';
import { OrganizationProvider } from '../../../../../clients/providers/Organization';
import BulkMoveToProject from './bulk_move_to_project';
import ToolbarButton from './ToolbarButton';
import * as translations from './strings';
import { getLangKey } from '../../../models/Language';
import { getApplication } from '../../../../../javascript/models/Application';
import {
  useMergeCompletion,
  MergeCompletionData
} from './hooks/useMergeCompletion';
import {
  handleBulkRequestSuccess,
  handleBulkRequestError,
  prepareForMerge
} from './handlers/handleBulkRequestResponse';

const strings = translations[getLangKey()];

export type BulkRequest = {
  id: number;
  completedAt: string | null;
  createdAt: string;
  details: {
    objects?: number[];
    success?: number[];
    targetState: UpdateState;
  };
  objectAction: string;
  relatedBulkRequestId: number;
};

type Props = {
  setSelectedTasks: (tasks: string[]) => void;
  selectedTasks: string[];
  getContainer?: () => HTMLDivElement;
  closable?: boolean;
  onClose: () => void;
  bulkActions: BulkRequest[];
  setActiveTask?: (taskId: number) => void;
  permissions: {
    [key: string]: boolean;
  };
  inline?: boolean;
  order?: string[];
  showSelectedCount?: boolean;
  containerClassName?: string;
  innerContainerClassName?: string;
  show?: boolean;
  clearable?: boolean;
  onBulkUpdateStart?: (payload: {
    taskIds: number[];
    targetState: UpdateState;
    source?: NewStateUpdateBulkRequestArgs['source'];
  }) => void;
  onBulkUpdateSuccess?: () => void | Promise<void>;
  onBulkUpdateError?: () => void;
  onSuccess?: (action: BulkRequestActions) => void;
  bulkRequestSource?: NewStateUpdateBulkRequestArgs['source'];
};

export type LastUpdate = {
  targetState: UpdateState;
  taskIds: number[];
  id?: number;
  actionsPerformed: BulkRequestActions[];
  source?: NewStateUpdateBulkRequestArgs['source'];
};

export const shouldCompleteDuplicateTaskUpdateImmediately = (
  actionsPerformed: BulkRequestActions[]
) =>
  !(
    actionsPerformed.includes('status_changed') ||
    actionsPerformed.includes('assigned') ||
    actionsPerformed.includes('tasks_merged')
  );

const BulkActionsBar: FC<Props> = ({
  setSelectedTasks,
  selectedTasks,
  getContainer,
  closable = true,
  onClose,
  bulkActions,
  setActiveTask,
  permissions: {
    editTaskStatus,
    editTaskAssignees,
    editTaskDueDate,
    editTaskTags,
    editTaskSeverity,
    billingRights,
    taskEditVisibility,
    taskMoveToProject,
    taskDelete,
    taskMerge
  },
  inline,
  order = [
    'editTaskStatus',
    'editTaskAssignees',
    'editTaskTags',
    'editTaskSeverity',
    'editTaskDueDate',
    'taskEditVisibility',
    'taskMoveToProject',
    'taskMerge',
    'taskDelete'
  ],
  showSelectedCount = true,
  containerClassName,
  innerContainerClassName,
  show = false,
  clearable = true,
  onBulkUpdateStart,
  onBulkUpdateSuccess,
  onBulkUpdateError,
  onSuccess,
  bulkRequestSource
}) => {
  const { project, loading } = useProjectState();
  const [status, setStatus] = useState<Status | undefined>();
  const [severity, setSeverity] = useState<Priority | undefined>();
  const [assignees, setAssignees] = useState<Assignee[]>([]);
  const [visibility, setVisibility] = useState<boolean | undefined>();
  const [selectedProject, setSelectedProject] = useState<number | undefined>();
  const [moving, setMoving] = useState<number | boolean>(false);
  const [deleteTasks, setDeleteTasks] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<number | boolean>(false);
  const [tags, setTags] = useState<TagType[]>([]);
  const [dueAt, setDueAt] = useState<string>('');
  const [lastBulkUpdate, setLastBulkUpdate] = useState<LastUpdate | null>(null);
  const [mergeTasks, setMergeTasks] = useState<boolean>(false);
  const [merging, setMerging] = useState<number | boolean>(false);
  bulkActions = bulkActions?.map(action =>
    camelcaseKeys(action, { deep: true })
  );

  const handleClear = () => {
    setStatus(undefined);
    setSeverity(undefined);
    setAssignees([]);
    setTags([]);
    setDueAt('');
    setVisibility(undefined);
    setSelectedProject(undefined);
    setDeleteTasks(false);
    setMergeTasks(false);
  };

  useEffect(() => {
    if (!selectedTasks.length) {
      handleClear();
    }
  }, [selectedTasks]);

  const showClearAndUpdate: boolean = !!(
    status ||
    severity ||
    assignees.length ||
    tags.length ||
    dueAt ||
    typeof visibility === 'boolean' ||
    selectedProject ||
    deleteTasks ||
    mergeTasks
  );

  const shouldNotifyDuplicateTaskUpdates = (
    actionsPerformed: BulkRequestActions[]
  ) => !shouldCompleteDuplicateTaskUpdateImmediately(actionsPerformed);

  const handleUpdate = () => {
    const targetState: UpdateState = {};
    const actionsPerformed: BulkRequestActions[] = [];

    const taskIds: number[] = selectedTasks.map(taskId => Number(taskId));

    if (deleteTasks) {
      targetState.deleteTask = deleteTasks;
      actionsPerformed.push('tasks_deleted');
      setDeleting(true);
    } else if (mergeTasks) {
      targetState.mergeTasks = mergeTasks;
      actionsPerformed.push('tasks_merged');
      setMerging(true);
    } else {
      if (status) {
        // @ts-expect-error
        targetState.statusId = status.statusId;
        targetState.columnId = status.columnId;
        actionsPerformed.push('status_changed');
      }

      if (severity) {
        targetState.priorityId = severity.id;
        actionsPerformed.push('severity_updated');
      }

      if (tags.length) {
        targetState.tagIds = tags.map(({ id }) => id);
        actionsPerformed.push('tagged');
      }
      if (assignees.length) {
        targetState.assigneeIds = assignees.map(({ id }) => id);
        actionsPerformed.push('assigned');
      }

      if (dueAt) {
        targetState.dueAt = dueAt;
        actionsPerformed.push('due_date_updated');
      }

      if (typeof visibility !== 'undefined') {
        targetState.guestVisible = visibility;
        actionsPerformed.push('guest_visible_updated');
      }

      if (selectedProject) {
        targetState.moveToProjectId = selectedProject;
        actionsPerformed.push('task_moved_to_project');
        setMoving(true);
      }
    }

    if (
      !deleteTasks &&
      !mergeTasks &&
      shouldNotifyDuplicateTaskUpdates(actionsPerformed)
    ) {
      onBulkUpdateStart?.({
        taskIds,
        targetState,
        source: bulkRequestSource
      });
    }

    setLastBulkUpdate({
      targetState,
      taskIds,
      actionsPerformed,
      source: bulkRequestSource
    });
  };

  const application = getApplication();

  const getRecentlyCreatedMergedTask = (taskIds: string[]) => {
    return application?.tasksCollection?.find(
      (model: { get: (key: string) => unknown }) => {
        const relatedIds = model.get('related_to_task_ids') as
          | {
              merged_from?: number[];
              mergedFrom?: number[];
            }
          | undefined;
        if (!relatedIds) return false;
        const mergedFrom = relatedIds.merged_from ?? relatedIds.mergedFrom;
        if (!mergedFrom?.length) return false;

        return mergedFrom.every((id: number) =>
          taskIds.includes(id.toString())
        );
      }
    );
  };

  const getMergedTaskForModal = (
    mergedTaskIds: string[],
    newTaskId?: number
  ) => {
    const fromMerge = getRecentlyCreatedMergedTask(mergedTaskIds);
    if (fromMerge) return fromMerge;
    if (newTaskId && application?.tasksCollection?.get) {
      return application.tasksCollection.get(newTaskId as never);
    }
    return null;
  };

  const showSuccessfulMergeModal = (
    mergedTaskIds: string[] = selectedTasks,
    mergeData?: MergeCompletionData
  ) => {
    const displayModal = () => {
      const task = getMergedTaskForModal(mergedTaskIds, mergeData?.newTaskId);
      const projectId = task?.get('project_id');
      const localTaskId = task?.get('local_task_id');
      const bugherdUrl = application?.get('bugherdUrl');
      const taskId = task?.get('id');

      const content = task ? (
        <>
          {strings.mergeSuccessDescription(mergedTaskIds.length)}
          <Button
            type="link"
            href={`${bugherdUrl}/projects/${projectId}/tasks/${localTaskId}`}
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
              event.preventDefault();
              setActiveTask?.(taskId);
            }}
            className={styles.mergeSuccessTaskLink}
          >
            #{localTaskId}
          </Button>
        </>
      ) : (
        strings.mergeSuccessDescription(mergedTaskIds.length)
      );

      Modal.success({
        title: strings.mergeSuccessTitle,
        content,
        onOk: () => task && setActiveTask?.(taskId),
        cancelButtonProps: {
          style: { display: 'none' }
        },
        getContainer,
        okText: strings.viewMergedTask,
        centered: true
      });
    };

    const task = getMergedTaskForModal(mergedTaskIds, mergeData?.newTaskId);
    if (task) {
      displayModal();
    } else {
      let retryCount = 0;
      const retryInterval = setInterval(() => {
        retryCount++;
        const foundTask = getMergedTaskForModal(
          mergedTaskIds,
          mergeData?.newTaskId
        );
        if (foundTask || retryCount >= 5) {
          clearInterval(retryInterval);
          displayModal();
        }
      }, 300);
    }
  };

  const MERGED_STATE_DISPLAY_MS = 2000;

  const onResetSelectedTasks = () => {
    setTimeout(() => {
      setLastBulkUpdate(null);
      handleClear();
      setSelectedTasks([]);
    }, 2500);
  };

  useEffect(() => {
    if (lastBulkUpdate && !lastBulkUpdate.id) {
      if (merging === true) prepareForMerge();
      processStateUpdateBulkRequest(
        lastBulkUpdate as NewStateUpdateBulkRequestArgs
      )
        .then(({ id }) => {
          handleBulkRequestSuccess({
            id,
            moving,
            merging,
            deleting,
            lastBulkUpdate,
            setMoving,
            setMerging,
            setDeleting,
            setLastBulkUpdate,
            onResetSelectedTasks,
            handleClear
          });

          if (
            shouldNotifyDuplicateTaskUpdates(lastBulkUpdate.actionsPerformed) &&
            !shouldCompleteDuplicateTaskUpdateImmediately(
              lastBulkUpdate.actionsPerformed
            )
          ) {
            return;
          }

          if (
            shouldCompleteDuplicateTaskUpdateImmediately(
              lastBulkUpdate.actionsPerformed
            )
          ) {
            void onBulkUpdateSuccess?.();
          }
        })
        .catch(() => {
          handleBulkRequestError(merging);

          if (
            shouldNotifyDuplicateTaskUpdates(lastBulkUpdate.actionsPerformed)
          ) {
            onBulkUpdateError?.();
          }
        });
    }
  }, [lastBulkUpdate]);

  const hasSameState = (targetState: UpdateState): boolean => {
    if (!lastBulkUpdate) return false;
    const targetKeys = Object.keys(targetState);
    const lastUpdateKeys = Object.keys(lastBulkUpdate.targetState);
    const matchingKeys =
      JSON.stringify(targetKeys.sort()) ===
      JSON.stringify(lastUpdateKeys.sort());
    if (!matchingKeys) return false;
    for (let key of targetKeys) {
      if (targetState[key] !== lastBulkUpdate.targetState[key]) return false;
    }
    return true;
  };

  const lastUpdateAction = bulkActions?.find(
    ({ id, details: { targetState, objects, success } }) => {
      return (
        id === lastBulkUpdate?.id ||
        (JSON.stringify((objects || success)?.sort()) ===
          JSON.stringify(lastBulkUpdate?.taskIds.sort()) &&
          hasSameState(targetState))
      );
    }
  );

  const onMergeComplete = useCallback(
    (data: MergeCompletionData) => {
      const taskIdsToMerge = (data.archivedTaskIds || []).map(String);
      setLastBulkUpdate(null);
      setMerging(false);
      showSuccessfulMergeModal(taskIdsToMerge, data);
      handleClear();
      setSelectedTasks([]);
      onClose();
      onSuccess?.('tasks_merged');
    },
    [showSuccessfulMergeModal, handleClear, setSelectedTasks, onClose]
  );

  useMergeCompletion(merging, onMergeComplete);

  const handleClose = () => {
    if (merging) {
      const app = getApplication() as { set?: (k: string, v: unknown) => void };
      app?.set?.('merging_bulk_request_id', null);
      app?.set?.('merging_pending', false);
    }
    handleClear();
    setLastBulkUpdate(null);
    onClose();
  };

  const showToolbar: boolean =
    !loading && (show || (project && selectedTasks.length > 1));

  const disableActions = loading || !project || selectedTasks.length <= 1;

  if (!showToolbar) return null;

  const limitReached: boolean = selectedTasks.length >= MAX_NUMBER_OF_TASKS;

  const getStatuses = () =>
    project.columns
      .filter(({ id }) => id !== 6)
      .map(({ columnId, name, statusId }) => ({
        columnId,
        name,
        statusId
      })) as Status[];

  const assignableUsers = getSortedAssignableUsers(
    project.users.filter(
      ({ member, accountConfirmed, role }) =>
        accountConfirmed && (member || role === 'guest')
    ) || [],
    project.assignGuests && project.organization.assignGuestsAllowed
  );

  const getActiveTags = () => project.tags.filter(({ active }) => active);

  const isDisabled = disableActions || deleteTasks || mergeTasks;

  const updating: boolean =
    deleting === true ||
    merging === true ||
    (lastBulkUpdate?.id !== deleting &&
      lastBulkUpdate?.id !== merging &&
      !!(lastUpdateAction && !lastUpdateAction.completedAt));

  const actionLabel = () => {
    let label = 'Update';
    if (deleteTasks) label = 'Delete';
    if (mergeTasks) label = 'Merge';
    if (selectedProject) label = 'Move';
    if (updating) label = label.replace(/.$/, 'ing');
    return label;
  };

  if (!project) return null;

  return (
    <OrganizationProvider id={project.organization.id}>
      <div
        className={cx(styles.container, containerClassName, {
          [styles.inline]: inline
        })}
      >
        <StatusBar
          taskCount={selectedTasks.length}
          bulkActions={bulkActions}
          lastUpdateAction={lastUpdateAction}
          lastBulkUpdate={lastBulkUpdate}
          updating={updating}
          merging={!!merging}
          deleting={!!deleting}
          moving={!!moving}
          onUndoComplete={handleClear}
          setLastBulkUpdate={setLastBulkUpdate}
          moved={lastBulkUpdate?.id === moving}
          deleted={lastBulkUpdate?.id === deleting}
          merged={false}
        />
        <div className={cx(styles.innerContainer, innerContainerClassName)}>
          {showSelectedCount && (
            <div className={styles.tasksSelected}>
              <span className={styles.count}>
                {selectedTasks.length} tasks selected
              </span>
              {limitReached && (
                <span className={styles.limitReached}>
                  &nbsp; (limit reached)
                </span>
              )}
            </div>
          )}
          {closable && (
            <div className={styles.closeContainer} onClick={handleClose}>
              <Close className={styles.closeIcon} />
            </div>
          )}
          <div className={styles.bulkButtons}>
            {order.map(action => {
              if (action === 'editTaskStatus' && editTaskStatus) {
                return (
                  <BulkStatus
                    onChange={setStatus}
                    statuses={getStatuses()}
                    status={status}
                    getContainer={getContainer}
                    disabled={isDisabled}
                  />
                );
              }
              if (action === 'editTaskAssignees' && editTaskAssignees) {
                return (
                  <BulkMultiple
                    values={assignees}
                    options={assignableUsers}
                    onChange={setAssignees}
                    getContainer={getContainer}
                    guestsAssignedToTasksAllowed={
                      project?.organization.assignGuestsAllowed
                    }
                    billingRights={billingRights}
                    isAssignees
                    disabled={isDisabled}
                  />
                );
              }
              if (action === 'editTaskTags' && editTaskTags) {
                return (
                  <BulkMultiple
                    values={tags}
                    options={getActiveTags() as TagType[]}
                    onChange={setTags}
                    getContainer={getContainer}
                    disabled={isDisabled}
                  />
                );
              }
              if (action === 'editTaskSeverity' && editTaskSeverity) {
                return (
                  <BulkSeverity
                    onChange={setSeverity}
                    priorities={project.priorities}
                    severity={severity}
                    getContainer={getContainer}
                    disabled={isDisabled}
                  />
                );
              }
              if (action === 'editTaskDueDate' && editTaskDueDate) {
                return (
                  <BulkDueDate
                    onChange={setDueAt}
                    dueAt={dueAt}
                    getContainer={getContainer}
                    disabled={isDisabled}
                  />
                );
              }
              if (action === 'taskEditVisibility' && taskEditVisibility) {
                return (
                  <BulkVisibility
                    onChange={setVisibility}
                    visibility={visibility}
                    getContainer={getContainer}
                    disabled={isDisabled}
                  />
                );
              }
              if (action === 'taskMoveToProject' && taskMoveToProject) {
                return (
                  <BulkMoveToProject
                    onChange={setSelectedProject}
                    getContainer={getContainer}
                    selectedProject={selectedProject}
                    disabled={isDisabled}
                  />
                );
              }
              if (action === 'taskMerge' && taskMerge) {
                return (
                  <ToolbarButton
                    active={mergeTasks}
                    onClick={() => setMergeTasks(!mergeTasks)}
                    label="Merge"
                    mergeButton
                    disabled={disableActions || deleteTasks}
                  />
                );
              }
              if (action === 'taskDelete' && taskDelete) {
                return (
                  <ToolbarButton
                    onClick={() => setDeleteTasks(!deleteTasks)}
                    active={deleteTasks}
                    deleteButton
                    disabled={disableActions || mergeTasks}
                  />
                );
              }
              return null;
            })}
          </div>
          <div
            className={cx(styles.clearAndUpdate, {
              [styles.hideClearAndUpdate]: !showClearAndUpdate,
              [styles.updating]: updating
            })}
          >
            {clearable && (
              <Button
                type="text"
                onClick={handleClear}
                className={cx(styles.clearButton, {
                  [styles.hideClear]: !showClearAndUpdate
                })}
              >
                Clear
              </Button>
            )}
            <Button
              type="primary"
              className={cx(styles.updateButton, {
                [styles.hideUpdate]: !showClearAndUpdate
              })}
              onClick={handleUpdate}
              disabled={updating}
              loading={updating}
            >
              {`${actionLabel()} tasks`}
            </Button>
          </div>
        </div>
      </div>
    </OrganizationProvider>
  );
};

export default BulkActionsBar;
